import { Trans, useLingui } from '@lingui/react/macro';
import { Button, Dialog, toast } from '@utima/ui';
import { Package } from 'lucide-react';
import { memo } from 'react';

import { IconWrapper } from '@/components/iconWrapper/IconWrapper';
import { trpc } from '@/services/trpc';

import { getFeedAgents } from './feedAgents';
import { useFeedMutation } from './useFeedMutation';

export type AddExistingFeedDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  chatId: string;
};

/**
 * Lists the organization's feeds this agent does not use yet. Adding one is a
 * new assignment only: nothing is imported or embedded again.
 */
export const AddExistingFeedDialog = memo(function AddExistingFeedDialog({
  open,
  onOpenChange,
  chatId,
}: AddExistingFeedDialogProps) {
  const { t } = useLingui();
  const {
    update: { mutateAsync: update, isPending, variables },
  } = useFeedMutation();

  const { data: feeds, isLoading } = trpc.feeds.findAll.useQuery(
    {
      populate: ['chats', 'productsCount'],
      pagination: { pageSize: 100 },
      orderBy: { name: 'asc' },
    },
    { enabled: open },
  );

  const available = (feeds?.items ?? [])
    .map(feed => {
      const agents = getFeedAgents(feed);

      return {
        feed,
        agentIds: agents.map(agent => agent.id),
        usedBy: agents.map(agent => agent.name).join(', '),
      };
    })
    .filter(({ agentIds }) => !agentIds.includes(chatId));

  const handleAdd = async (feedId: string, agentIds: string[]) => {
    try {
      await update({ id: feedId, chatIds: [...agentIds, chatId] });
      toast.success(t`Feed added to this agent`);
      onOpenChange(false);
    } catch (error) {
      toast.error(t`Failed to add feed`, {
        description: error instanceof Error ? error.message : undefined,
      });
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Content className='max-h-[90vh] w-[600px] overflow-y-auto'>
        <Dialog.Header>
          <Dialog.Title>
            <Trans>Add existing product feed</Trans>
          </Dialog.Title>
          <Dialog.Description>
            <Trans>
              The agent starts using the feed&apos;s products right away. The
              feed is not imported again, and its settings and attributes stay
              shared with the agents already using it.
            </Trans>
          </Dialog.Description>
        </Dialog.Header>

        <div className='space-y-2 py-4'>
          {!isLoading && available.length === 0 && (
            <p className='text-sm text-gray-500'>
              <Trans>
                Every product feed in this organization is already used by this
                agent.
              </Trans>
            </p>
          )}
          {available.map(({ feed, agentIds, usedBy }) => (
            <div
              key={feed.id}
              className='flex items-center justify-between gap-3 rounded-md border border-border p-3'
            >
              <div className='flex min-w-0 items-center gap-3'>
                <IconWrapper size='sm' className='text-danger' Icon={Package} />
                <div className='min-w-0'>
                  <p className='truncate font-medium'>{feed.name}</p>
                  <p className='truncate text-xs text-gray-500'>
                    <Trans>
                      {feed.productsCount} products · used by {usedBy}
                    </Trans>
                  </p>
                </div>
              </div>
              <Button
                size='sm'
                variant='primary'
                outline
                loading={isPending && variables?.id === feed.id}
                disabled={isPending}
                onClick={() => handleAdd(feed.id, agentIds)}
              >
                <Trans>Add</Trans>
              </Button>
            </div>
          ))}
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
});
